import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Download, Lock, AlertCircle, CheckCircle } from 'lucide-react';
import { apiService } from '../services/api';

const DownloadApp: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isLoadingApp, setIsLoadingApp] = useState(false);
  const [error, setError] = useState('');
  const [appInfo, setAppInfo] = useState<any>(null);
  const [appData, setAppData] = useState<any>(null);

  // Processar parâmetro 'app' da URL quando a página carregar
  useEffect(() => {
    const appId = searchParams.get('app');
    if (appId) {
      loadAppInfo(appId);
    }
  }, [searchParams]);

  const loadAppInfo = async (appId: string) => {
    setIsLoadingApp(true);
    setError('');
    
    try {
      // Buscar informações do app
      const appsResponse = await apiService.getAppDownloads();
      const apps = appsResponse?.data || appsResponse || [];
      const app = apps.find((a: any) => a._id === appId);
      
      if (app) {
        setAppData(app);
        // Se o app tem código de download, mostrar mensagem informativa
        if (app.download_code) {
          setError('');
        }
      } else {
        setError('Aplicativo não encontrado');
      }
    } catch (err: any) {
      console.error('Erro ao carregar informações do app:', err);
      setError('Erro ao carregar informações do aplicativo');
    } finally {
      setIsLoadingApp(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setCode(value);
    setError('');
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!code || code.length < 5) {
      setError('Digite um código válido (5 caracteres)');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const response = await apiService.verifyAppDownloadCode(code);
      
      if (response.success) {
        setAppInfo(response.data);
        // Aguardar 1 segundo para mostrar informações e depois iniciar download
        setTimeout(() => {
          handleDownload(response.data.appId, response.data.versionId, code);
        }, 1500);
      } else {
        setError(response.error || 'Código inválido');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Código inválido ou expirado');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleDownload = (appId: string, versionId: string, downloadCode: string) => {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://api-cms.cms20.online';
    const downloadUrl = `${apiUrl}/api/apps/download/${appId}/${versionId}/${downloadCode}`;
    window.location.href = downloadUrl;
    
    // Mostrar mensagem de sucesso
    setTimeout(() => {
      setAppInfo(null);
      setCode('');
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <Download className="h-8 w-8 text-blue-600" />
          </div>
          <CardTitle className="text-2xl">Download de Aplicativo</CardTitle>
          <CardDescription>
            {appData 
              ? `Baixar ${appData.name} - Digite o código de acesso`
              : 'Digite o código de acesso fornecido pelo seu revendedor'
            }
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {isLoadingApp ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Carregando informações do aplicativo...</span>
            </div>
          ) : !appInfo ? (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              {appData && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">{appData.name}</h4>
                  <p className="text-sm text-gray-600">{appData.description}</p>
                  {appData.platforms && appData.platforms.length > 0 && (
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {appData.platforms.map((platform: string) => (
                        <span key={platform} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {platform}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Código de Acesso
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={handleCodeChange}
                  placeholder="A2B3C"
                  maxLength={5}
                  className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase"
                  disabled={isVerifying}
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Digite o código de 5 caracteres
                </p>
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={isVerifying || code.length < 5}
                className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {isVerifying ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Verificando...
                  </>
                ) : (
                  <>
                    <Lock className="h-5 w-5" />
                    Verificar Código
                  </>
                )}
              </button>

              <div className="text-center pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  Não tem um código de acesso?
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Entre em contato com seu revendedor para obter o código
                </p>
              </div>
            </form>
          ) : (
            <div className="space-y-4 animate-in fade-in duration-500">
              <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="h-8 w-8 text-green-600 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-green-900">Código Válido!</h3>
                  <p className="text-sm text-green-700">Iniciando download...</p>
                </div>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Informações do Aplicativo:</h4>
                <div className="space-y-1 text-sm">
                  <p><span className="font-medium">Nome:</span> {appInfo.appName}</p>
                  <p><span className="font-medium">Versão:</span> {appInfo.version}</p>
                  <p><span className="font-medium">Plataformas:</span> {appInfo.platforms.join(', ')}</p>
                </div>
              </div>

              <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span>Preparando download...</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Informações adicionais */}
      <div className="absolute bottom-4 left-0 right-0 text-center">
        <p className="text-xs text-gray-500">
          Download seguro e protegido por código de acesso
        </p>
      </div>
    </div>
  );
};

export default DownloadApp;

